import { useState, useEffect } from 'react';
import { Target, Buildings } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadScoresPage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [country, setCountry] = useState('');
  const [minScore, setMinScore] = useState('');

  useEffect(() => {
    const params = new URLSearchParams();
    if (country) params.set('country', country);
    if (minScore) params.set('min_score', minScore);

    fetch(`${API}/lead-scores?${params}`, { credentials: 'include' })
      .then(r => r.json())
      .then(setLeads)
      .finally(() => setLoading(false));
  }, [country, minScore]);

  return (
    <div className="animate-fade-in" data-testid="lead-scores-page">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-black tracking-tight">Lead Scores</h1>
        <p className="text-sm text-zinc-500 mt-1">Ranked facilities by commercial priority</p>
      </div>

      {/* Filters */}
      <div className="border border-zinc-200 bg-white p-4 mb-6">
        <div className="flex items-center gap-3">
          <Select value={country} onValueChange={v => setCountry(v === 'all' ? '' : v)}>
            <SelectTrigger data-testid="lead-country-filter" className="w-48 rounded-sm text-sm"><SelectValue placeholder="Country" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Countries</SelectItem>
              <SelectItem value="NL">Netherlands</SelectItem>
              <SelectItem value="BE">Belgium</SelectItem>
              <SelectItem value="FR">France</SelectItem>
              <SelectItem value="DE">Germany</SelectItem>
            </SelectContent>
          </Select>
          <Select value={minScore} onValueChange={v => setMinScore(v === 'all' ? '' : v)}>
            <SelectTrigger data-testid="lead-min-score-filter" className="w-48 rounded-sm text-sm"><SelectValue placeholder="Min Score" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any Score</SelectItem>
              <SelectItem value="90">90+</SelectItem>
              <SelectItem value="80">80+</SelectItem>
              <SelectItem value="60">60+</SelectItem>
              <SelectItem value="40">40+</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Lead Score Cards */}
      <div className="space-y-px border border-zinc-200" data-testid="lead-scores-list">
        {loading ? (
          <div className="bg-white p-12 text-center text-zinc-400">Loading...</div>
        ) : leads.length === 0 ? (
          <div className="bg-white p-12 text-center text-zinc-400">No leads found</div>
        ) : (
          leads.map((l, i) => (
            <div key={l.facility_id} data-testid={`lead-row-${l.facility_id}`} className="bg-white flex items-center gap-4 px-5 py-4 hover:bg-zinc-50 transition-colors duration-150">
              <div className="font-data text-lg font-bold text-zinc-300 w-8">#{i + 1}</div>
              <div className={`w-16 h-16 flex items-center justify-center rounded-sm ${l.lead_score >= 70 ? 'bg-emerald-50' : l.lead_score >= 40 ? 'bg-amber-50' : 'bg-red-50'}`}>
                <span className={`font-data text-2xl font-bold ${l.lead_score >= 70 ? 'text-emerald-600' : l.lead_score >= 40 ? 'text-amber-600' : 'text-red-500'}`}>
                  {l.lead_score}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm">{l.name}</div>
                <div className="text-xs text-zinc-500">{l.city}, {l.country_code}</div>
              </div>
              <Badge className="rounded-full px-2 py-0.5 text-xs font-data bg-zinc-100 text-zinc-700">{l.facility_type}</Badge>
              <div className="flex items-center gap-3 text-xs">
                {l.has_technical_contact && <Badge className="rounded-full px-2 py-0.5 text-xs font-data bg-blue-100 text-blue-800">Tech Contact</Badge>}
                {l.export_eligible && <Badge className="rounded-full px-2 py-0.5 text-xs font-data bg-emerald-100 text-emerald-800">Exportable</Badge>}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
